import AdsStateHelper from '../Ads/AdsStateHelper';
import { IContextResponse, ContextType, ResponseStatus, ResponseType } from '../ContextType';
import GameStateHelper from '../GameStateHelper';
import { IGameState } from '../IGameState';
import { BotOptStatus } from '../IUserState';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import Game from 'Game';
import UIStateHelper from 'State/UI/UIStateHelper';
import { PopupID } from 'State/UI/PopupID';
import { AdvertisementType } from 'Utility/SDK/Advertisement';
import { InfoMenuType } from 'System/UI/Menu/InfoMenuHandler';
import TimerStateHelper from 'State/Timer/TimerStateHelper';
import { TimerType } from 'System/Timer/TimerTypes';
import DateHelper from 'Utility/DateHelper';
import { IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { IRewardData, RewardType } from 'Utility/RewardHelper';
import { LevelStatus } from 'State/Level/LevelStatus';
import { SocialStateHelper } from 'State/Player/Social/SocialStateHelper';
import { PlayerStateHelper } from 'State/Player/PlayerStateHelper';
import { PlayerTask } from 'State/Player/PlayerTasks';
import SDKIntegrationsHelper from 'Utility/SDK/SDKIntegrationsHelper';

export default class ContextStateHelper {
    public static restoreContext(state: IGameState, response: IContextResponse): IGameState {
        state = {
            ...state,
            gamePaused: false,
            context: {
                current: ContextType.DEFAULT,
                audioEnabled: true,
                response: response,
            },
        };

        if (response.status === ResponseStatus.FAIL) {
            // TODO: Handle restore context fail state.
            switch (response.responseType) {
                case ResponseType.SUBSCRIBE_BOT_OPT_IN:
                    Game.sdk.gameTracking(FBTrackingEvent.Bot_deny);
                    return {
                        ...state,
                        userState: {
                            ...state.userState,
                            subscribeBot: BotOptStatus.DISMISSED,
                        },
                        UIState: UIStateHelper.hidePopup(state.UIState, PopupID.BOT_OPT_IN_MENU, null),
                    };

                case ResponseType.DISPLAY_ADVERTISEMENT:
                    if (response.data.code === 'USER_INPUT') {
                        return state;
                    }
                    if (response.data.placement.type === AdvertisementType.INTERSTITIAL) {
                        return AdsStateHelper.handleInterstitial(state, response.data);
                    } else {
                        Game.sdk.gameTracking(FBTrackingEvent.adRequestFailed_Rewarded);
                    }

                    return {
                        ...state,
                        UIState: UIStateHelper.showPopup(state.UIState, PopupID.INFO_MENU, {
                            type: InfoMenuType.NO_ADS,
                        }),
                    };

                case ResponseType.PURCHASE_PRODUCT:
                    return {
                        ...state,
                        UIState: UIStateHelper.showPopup(state.UIState, PopupID.INFO_MENU, {
                            type: InfoMenuType.ERROR,
                            error: response.data?.errorMsg || response.data || "Unknown error",
                        }),
                    };
                case ResponseType.SHARE_3_STAR_REWARD:
                    state = AdsStateHelper.checkDisplayInterstitial(state);

                    return GameStateHelper.endLevel(state, {
                        status: LevelStatus.COMPLETE,
                        firstComplete: response.data.firstComplete,
                    });

                default:
                    return state;
            }
        }

        switch (response.responseType) {
            case ResponseType.PURCHASE_PRODUCT:
                state = TimerStateHelper.setTimer(state, TimerType.LAST_PURCHASE, DateHelper.now);
                return {
                    ...state,
                    ...IAPStateHelper.consumePurchase(state, response.data.product),

                    // Close the shop pop-up & show firstpurchase if it is first
                    UIState: Object.assign(
                        UIStateHelper.hidePopup(state.UIState, PopupID.SHOP_MENU, null),
                        state.playerState.purchases === 0
                            ? UIStateHelper.showPopup(state.UIState, PopupID.THANK_YOU_FIRST_PURCHASE, {
                                  amount: (
                                      response.data.product.rewards.find(
                                          (e: IRewardData) => e.type === RewardType.COINS
                                      ) || { value: 0 }
                                  ).value,
                              })
                            : {}
                    ),
                };
            case ResponseType.SHARE_BEAT_HIGHSCORE_LEVEL:
                return {
                    ...state,
                    UIState: UIStateHelper.hidePopup(state.UIState, 'scoreBeatenMenu', null),
                };
            case ResponseType.SEND_CURRENCY:
                return {
                    ...state,
                    playerState: SocialStateHelper.sendCurrency(state.playerState, response.data),
                };
            case ResponseType.SEND_FRIEND_GIFT:
                return {
                    ...state,
                    playerState: SocialStateHelper.sendFriendGift(state.playerState, response.data),
                };
            case ResponseType.REQUEST_CURRENCY:
                return {
                    ...state,
                    playerState: SocialStateHelper.requestCurrency(state.playerState, response.data),
                };

            case ResponseType.GIVE_CURRENCY:
                return {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, [
                        {
                            task: PlayerTask.SHOW_POPUP,
                            popup: PopupID.SEND_GIFT_MENU,
                            meta: {
                                payload: response.data.payload,
                                reward: response.data.reward,
                            },
                            delay: 1,
                        },
                    ]),
                };
            case ResponseType.SHARE_3_STAR_REWARD:
                return GameStateHelper.endLevel(state, {
                    status: LevelStatus.COMPLETE,
                    firstComplete: response.data.firstComplete,
                });
            case ResponseType.DISPLAY_ADVERTISEMENT:
                return AdsStateHelper.adComplete(state, response.data);
            case ResponseType.SUBSCRIBE_BOT_OPT_IN:
                return {
                    ...state,
                    playerState: PlayerStateHelper.subscribeBotOptIn(state.playerState),
                    userState: {
                        ...state.userState,
                        subscribeBot: BotOptStatus.SUBSCRIBED,
                    },
                    UIState: UIStateHelper.hidePopup(state.UIState, PopupID.BOT_OPT_IN_MENU, null),
                };
        }

        return state;
    }

    public static setContext(state: IGameState, context: ContextType, data: any) {
        let audioEnabled: boolean = true;
        switch (context) {
            case ContextType.DEFAULT:
                break;
            case ContextType.PURCHASE_PRODUCT:
                SDKIntegrationsHelper.handlePurchaseProduct(state, data);
                break;
            case ContextType.INVITE_FRIEND:
                SDKIntegrationsHelper.inviteFriend(data as string);
                break;
            case ContextType.SEND_CURRENCY:
                SDKIntegrationsHelper.sendCurrency(data.friendId);
                break;
            case ContextType.SEND_FRIEND_GIFT:
                SDKIntegrationsHelper.sendGift(state, data);
                break;
            case ContextType.GIVE_CURRENCY:
                SDKIntegrationsHelper.giveCurrency(data);
                break;
            case ContextType.REQUEST_CURRENCY:
                SDKIntegrationsHelper.requestCurrency(data.friendId);
                break;
            case ContextType.SHARE_FIELD_UNLOCK:
                SDKIntegrationsHelper.shareFieldUnlock(data.field);
                break;
            case ContextType.SHARE_SCREENSHOT:
                SDKIntegrationsHelper.shareScreenshot(data.message, data.payloadData);
                break;
            case ContextType.SHARE_CROP_UNLOCKED:
                SDKIntegrationsHelper.shareCropUnlock(data.field);
                break;
            case ContextType.SHARE_PET_UNLOCKED:
                SDKIntegrationsHelper.sharePetUnlock(data.pet);
                break;
            case ContextType.SHARE_3_STAR_REWARD:
                SDKIntegrationsHelper.share3StarReward(data);
                break;
            case ContextType.SHARE_BEAT_HIGHSCORE_LEVEL:
                SDKIntegrationsHelper.shareBeatHighscoreLevel(data.levelNumber, data.playerEntry, data.friendEntry);
                break;
            case ContextType.DISPLAY_ADVERTISEMENT:
                audioEnabled = false;
                SDKIntegrationsHelper.displayAdvertisement(data.placement, state, data.payload);
                break;
            case ContextType.SUBSCRIBE_BOT_OPT_IN:
                SDKIntegrationsHelper.subscribeBotOptIn();
                break;
            case ContextType.SHARE_HARD_LEVEL:
                SDKIntegrationsHelper.shareHardLevel(data);
                break;
            case ContextType.SHARE_FARM:
                SDKIntegrationsHelper.shareFarm();
                break;
        }

        return {
            ...state,
            gamePaused: true,
            context: {
                current: context,
                audioEnabled: audioEnabled,
                request: {
                    context: context,
                    data: data,
                },
            },
        };
    }
}
